import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * In-app confirm / prompt, as a promise.
 *
 * WHY THIS EXISTS — `window.confirm` and `window.prompt` are unsafe for steps the
 * workflow depends on:
 *
 *  - Browsers suppress them. After a page has shown a few, Chrome offers
 *    "Prevent this page from creating additional dialogs"; once ticked, every
 *    later confirm() returns false and prompt() returns null with NO dialog and
 *    no error. The calling code reads that as "user cancelled" and silently does
 *    nothing. That is the most likely cause of the reported "Revise decision does
 *    nothing" (R1): the button ran, confirm() returned false, and the handler
 *    returned before navigating.
 *  - They cannot be styled, so a quality-risk acknowledgement looks like a
 *    browser error rather than a deliberate sign-off.
 *  - Playwright has to install a dialog handler to get past them, so the risk
 *    gate could not be covered by an end-to-end test that reads the text.
 *
 * Usage:
 *   const ask = useAsk();
 *   const ok   = await ask({ title, body });                    // → true | null
 *   const text = await ask({ title, body, requireText: true }); // → string | null
 *
 * Resolves null when cancelled. With `requireText`, the confirm button stays
 * disabled until something non-blank is typed, so an empty acknowledgement
 * cannot be submitted.
 */
const AskCtx = createContext(null);

export function AskProvider({ children }) {
  const [req, setReq] = useState(null);
  const [text, setText] = useState('');
  const resolverRef = useRef(null);
  const inputRef = useRef(null);
  const confirmRef = useRef(null);

  const ask = useCallback((options) => {
    // Settle any dialog already open before replacing it. Overwriting
    // resolverRef dropped the previous resolve on the floor, so the earlier
    // `await ask(...)` hung forever with no dialog left to settle it — an
    // unrecoverable stuck handler. Concurrent asks are reachable in practice: a
    // confirm-only dialog leaves focus on its trigger, so Enter re-fires it.
    if (resolverRef.current) {
      const stale = resolverRef.current;
      resolverRef.current = null;
      stale(null);
    }
    setText(options?.defaultText || '');
    setReq(options || {});
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setReq(null);
    setText('');
    if (resolve) resolve(value);
  }, []);

  // Move focus INTO the dialog when it opens: the text field when there is one,
  // otherwise the confirm button.
  //
  // Previously only the text field was focused, so a confirm-only dialog left
  // focus on the button that opened it — pressing Enter re-fired that button,
  // opening a second dialog and orphaning the first promise. It also left
  // keyboard and screen-reader users outside a modal they could not see they were
  // outside of.
  useEffect(() => {
    if (!req) return;
    if (inputRef.current) inputRef.current.focus();
    else if (confirmRef.current) confirmRef.current.focus();
  }, [req]);

  // Settle on unmount so a caller awaiting this promise can never hang, and a
  // dialog can never outlive the component that opened it — previously, opening
  // the risk dialog and switching tabs left the modal on screen over the new tab
  // and resumed a handler whose component was gone.
  useEffect(
    () => () => {
      const stale = resolverRef.current;
      resolverRef.current = null;
      if (stale) stale(null);
    },
    []
  );

  // Escape cancels. Deliberately no click-outside-to-cancel: these dialogs guard
  // decisions that are hard to walk back, and a stray click should not dismiss
  // one silently.
  useEffect(() => {
    if (!req) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') settle(null);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [req, settle]);

  const needsText = !!req?.requireText;
  const canConfirm = !needsText || text.trim().length > 0;

  return (
    <AskCtx.Provider value={ask}>
      {children}
      {req && (
        <div
          className="ask-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={req.title || 'Confirm'}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            className="card"
            style={{ maxWidth: 520, width: '90%', padding: 20, background: '#fff' }}
          >
            {req.title && (
              <h3 style={{ margin: '0 0 8px', fontSize: 16 }} data-testid="ask-title">
                {req.title}
              </h3>
            )}
            {req.body && (
              <div
                style={{ fontSize: 13, color: 'var(--ink-700)', whiteSpace: 'pre-wrap', marginBottom: 12 }}
                data-testid="ask-body"
              >
                {req.body}
              </div>
            )}
            {req.warning && (
              <div className="banner review" style={{ marginBottom: 12 }} data-testid="ask-warning">
                {req.warning}
              </div>
            )}
            {needsText && (
              <textarea
                ref={inputRef}
                data-testid="ask-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={req.placeholder || 'Reason'}
                rows={3}
                style={{ width: '100%', marginBottom: 12 }}
              />
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-sm" data-testid="ask-cancel" onClick={() => settle(null)}>
                {req.cancelLabel || 'Cancel'}
              </button>
              <button
                className={'btn btn-sm ' + (req.danger ? 'btn-danger' : 'btn-primary')}
                ref={confirmRef}
                data-testid="ask-confirm"
                disabled={!canConfirm}
                title={canConfirm ? undefined : 'A reason is required'}
                onClick={() => settle(needsText ? text.trim() : true)}
              >
                {req.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AskCtx.Provider>
  );
}

export const useAsk = () => useContext(AskCtx);
